import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import {
    clearLeaseRecordsFieldError,
    getLeaseRecordsFieldErrors,
    ILeaseRecordsApiError,
    LeaseRecordsErrorAlert,
    LeaseRecordsFieldError,
    parseLeaseRecordsApiError,
} from './LeaseRecordsApiError';
import useLatestRequest from './useLatestRequest';

interface ILeaseKind {
    code: string;
    dscr: string;
}
interface IClient {
    clNo: number;
    name1?: string;
}
interface IProject {
    prNo: string;
    prName?: string;
    clNo: number;
}
interface ILookupOption {
    value: string;
    label: string;
}
interface IProps {
    isOpen: boolean;
    leaseKinds: ILeaseKind[];
    onClose: () => void;
    onCreated: (leaseNumber: number) => void;
}

const CreateManualLeaseModal: React.FC<IProps> = ({ isOpen, leaseKinds, onClose, onCreated }) => {
    const { get, post, response } = useFetch();
    const [prefix, setPrefix] = useState('59');
    const [leaseNumber, setLeaseNumber] = useState('');
    const [kind, setKind] = useState('');
    const [client, setClient] = useState<ILookupOption | null>(null);
    const [project, setProject] = useState<ILookupOption | null>(null);
    const [lessee, setLessee] = useState<ILookupOption | null>(null);
    const [lessorName, setLessorName] = useState('');
    const [clientSearch, setClientSearch] = useState('');
    const [projectSearch, setProjectSearch] = useState('');
    const [lesseeSearch, setLesseeSearch] = useState('');
    const [clientOptions, setClientOptions] = useState<ILookupOption[]>([]);
    const [projectOptions, setProjectOptions] = useState<ILookupOption[]>([]);
    const [lesseeOptions, setLesseeOptions] = useState<ILookupOption[]>([]);
    const [activePicker, setActivePicker] = useState<string | null>(null);
    const [error, setError] = useState<ILeaseRecordsApiError | null>(null);
    const [saving, setSaving] = useState(false);
    const clientRequests = useLatestRequest();
    const projectRequests = useLatestRequest();
    const lesseeRequests = useLatestRequest();
    const createRequests = useLatestRequest();

    useEffect(() => {
        if (isOpen) {
            setPrefix('59');
            setLeaseNumber('');
            setKind('');
            setClient(null);
            setProject(null);
            setLessee(null);
            setLessorName('');
            setClientSearch('');
            setProjectSearch('');
            setLesseeSearch('');
            setClientOptions([]);
            setProjectOptions([]);
            setLesseeOptions([]);
            setActivePicker(null);
            setError(null);
        } else {
            createRequests.invalidate();
        }
    }, [createRequests, isOpen]);

    useEffect(() => {
        if (!isOpen || client) {
            clientRequests.invalidate();
            return undefined;
        }
        const timer = setTimeout(async () => {
            const requestId = clientRequests.begin();
            const data = await get(`/v1.0/leases/admin/clients?skip=0&top=10&search=${encodeURIComponent(clientSearch.trim())}`);
            if (!clientRequests.isCurrent(requestId)) {
                return;
            }
            setClientOptions(
                Array.isArray(data?.items)
                    ? data.items.map((item: IClient) => ({
                          value: item.clNo.toString(),
                          label: `${item.clNo} — ${item.name1 || 'Unnamed client'}`,
                      }))
                    : []
            );
        }, 300);
        return () => clearTimeout(timer);
    }, [client, clientRequests, clientSearch, get, isOpen]);

    useEffect(() => {
        if (!isOpen || !client || project) {
            projectRequests.invalidate();
            return undefined;
        }
        const timer = setTimeout(async () => {
            const requestId = projectRequests.begin();
            const data = await get(
                `/v1.0/leases/admin/prospects?skip=0&top=10&clientNumber=${client.value}&search=${encodeURIComponent(projectSearch.trim())}`
            );
            if (!projectRequests.isCurrent(requestId)) {
                return;
            }
            setProjectOptions(
                Array.isArray(data?.items)
                    ? data.items.map((item: IProject) => ({
                          value: item.prNo,
                          label: `${item.prNo} — ${item.prName || 'Unnamed project'}`,
                      }))
                    : []
            );
        }, 300);
        return () => clearTimeout(timer);
    }, [client, get, isOpen, project, projectRequests, projectSearch]);

    useEffect(() => {
        if (!isOpen || lessee) {
            lesseeRequests.invalidate();
            return undefined;
        }
        const timer = setTimeout(async () => {
            const requestId = lesseeRequests.begin();
            const data = await get(`/v1.0/leases/master/lessees?search=${encodeURIComponent(lesseeSearch.trim())}`);
            if (!lesseeRequests.isCurrent(requestId)) {
                return;
            }
            setLesseeOptions(Array.isArray(data) ? data.map((item: string) => ({ value: item, label: item })) : []);
        }, 300);
        return () => clearTimeout(timer);
    }, [get, isOpen, lessee, lesseeRequests, lesseeSearch]);

    if (!isOpen) {
        return null;
    }

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        const normalizedNumber = leaseNumber.replace(/-/g, '').trim();
        if (!/^\d+$/.test(normalizedNumber) || !normalizedNumber.startsWith(prefix)) {
            setError(
                parseLeaseRecordsApiError({
                    title: `Enter the complete numeric lease number beginning with ${prefix}.`,
                    errors: { leaseNumber: [`Enter a number beginning with ${prefix}.`] },
                })
            );
            return;
        }
        if (!kind) {
            setError(parseLeaseRecordsApiError({ title: 'Lease kind is required.', errors: { kind: ['Select a lease kind.'] } }));
            return;
        }
        const normalizedLessorName = lessorName.trim();
        if (prefix === '59' && !normalizedLessorName) {
            setError(
                parseLeaseRecordsApiError({
                    title: 'Primary lessor name is required for a 59 lease.',
                    errors: { lessorName: ['Enter the primary lessor name.'] },
                })
            );
            return;
        }
        setSaving(true);
        const requestId = createRequests.begin();
        const result = await post('/v1.0/leases/master/manual', {
            leaseNumber: Number(normalizedNumber),
            prefix,
            kind,
            clientNumber: client ? Number(client.value) : null,
            projectNumber: project ? Number(project.value) : null,
            lessee: lessee?.value || null,
            lessorName: normalizedLessorName || null,
        });
        if (!createRequests.isCurrent(requestId)) {
            return;
        }
        setSaving(false);
        if (response.ok && result?.leaseNumber) {
            onCreated(result.leaseNumber);
        } else {
            setError(parseLeaseRecordsApiError(result || response.data, response.status));
        }
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <form style={styles.modal} onSubmit={submit} onClick={(event) => event.stopPropagation()}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>New Manual Lease Record</h2>
                        <p style={styles.subtitle}>Creates a Lease Records entry only; it does not start a KUDU in-hand lease.</p>
                    </div>
                    <button type="button" onClick={onClose} style={styles.close} aria-label="Close">
                        ×
                    </button>
                </div>
                <div style={styles.body}>
                    <p style={styles.help}>
                        Numbers are not auto-generated. Select the business-defined prefix and enter the complete unique lease number.
                    </p>
                    <div style={styles.grid}>
                        <label style={styles.label}>
                            Prefix *
                            <select value={prefix} onChange={(e) => setPrefix(e.target.value)} style={styles.input}>
                                <option value="59">59</option>
                                <option value="62">62</option>
                                <option value="82">82</option>
                            </select>
                        </label>
                        <label style={styles.label}>
                            Complete Lease Number *
                            <LeaseRecordInput
                                value={leaseNumber}
                                onChange={(e) => {
                                    setLeaseNumber(e.target.value);
                                    setError((current) => clearLeaseRecordsFieldError(current, 'leaseNumber'));
                                }}
                                placeholder={`${prefix}...`}
                                style={styles.input}
                                autoFocus={true}
                                aria-invalid={getLeaseRecordsFieldErrors(error, 'leaseNumber').length > 0}
                                aria-describedby={getLeaseRecordsFieldErrors(error, 'leaseNumber').length ? 'manual-lease-number-error' : undefined}
                            />
                            <LeaseRecordsFieldError id="manual-lease-number-error" messages={getLeaseRecordsFieldErrors(error, 'leaseNumber')} />
                        </label>
                        <label style={styles.label}>
                            Lease Kind *
                            <select
                                value={kind}
                                onChange={(e) => {
                                    setKind(e.target.value);
                                    setError((current) => clearLeaseRecordsFieldError(current, 'kind'));
                                }}
                                style={styles.input}
                                aria-invalid={getLeaseRecordsFieldErrors(error, 'kind').length > 0}
                                aria-describedby={getLeaseRecordsFieldErrors(error, 'kind').length ? 'manual-lease-kind-error' : undefined}
                            >
                                <option value="">Select a kind</option>
                                {leaseKinds
                                    .filter((item) => item.code !== 'K')
                                    .map((item) => (
                                        <option key={item.code} value={item.code}>
                                            {item.dscr} ({item.code})
                                        </option>
                                    ))}
                            </select>
                            <LeaseRecordsFieldError id="manual-lease-kind-error" messages={getLeaseRecordsFieldErrors(error, 'kind')} />
                        </label>
                        <SearchPicker
                            label="Client"
                            placeholder="Search by client name or number..."
                            search={clientSearch}
                            selected={client}
                            options={clientOptions}
                            isOpen={activePicker === 'client'}
                            onOpen={() => setActivePicker('client')}
                            onSearch={(value) => {
                                setClientSearch(value);
                                setClient(null);
                                setProject(null);
                                setProjectSearch('');
                            }}
                            onSelect={(option) => {
                                setClient(option);
                                setClientSearch(option.label);
                                setProject(null);
                                setProjectSearch('');
                                setActivePicker(null);
                            }}
                        />
                        <SearchPicker
                            label="Project"
                            placeholder={client ? 'Search by project name or number...' : 'Select a client first'}
                            search={projectSearch}
                            selected={project}
                            options={projectOptions}
                            disabled={!client}
                            isOpen={activePicker === 'project'}
                            onOpen={() => setActivePicker('project')}
                            onSearch={(value) => {
                                setProjectSearch(value);
                                setProject(null);
                            }}
                            onSelect={(option) => {
                                setProject(option);
                                setProjectSearch(option.label);
                                setActivePicker(null);
                            }}
                        />
                        <SearchPicker
                            label="Lessee"
                            placeholder="Search existing lessees..."
                            search={lesseeSearch}
                            selected={lessee}
                            options={lesseeOptions}
                            isOpen={activePicker === 'lessee'}
                            onOpen={() => setActivePicker('lessee')}
                            onSearch={(value) => {
                                setLesseeSearch(value);
                                setLessee(null);
                            }}
                            onSelect={(option) => {
                                setLessee(option);
                                setLesseeSearch(option.label);
                                setActivePicker(null);
                            }}
                        />
                        <label style={styles.label}>
                            Primary Lessor Name{prefix === '59' ? ' *' : ''}
                            <LeaseRecordInput
                                value={lessorName}
                                onChange={(e) => setLessorName(e.target.value)}
                                placeholder="Enter the primary lessor name"
                                style={styles.input}
                            />
                        </label>
                    </div>
                    <LeaseRecordsErrorAlert error={error} onDismiss={() => setError(null)} />
                </div>
                <div style={styles.footer}>
                    <button type="button" onClick={onClose} style={styles.secondary}>
                        Cancel
                    </button>
                    <button type="submit" disabled={saving} style={styles.primary}>
                        {saving ? 'Creating...' : 'Create Lease Record'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const SearchPicker: React.FC<{
    label: string;
    placeholder: string;
    search: string;
    selected: ILookupOption | null;
    options: ILookupOption[];
    disabled?: boolean;
    isOpen: boolean;
    onOpen: () => void;
    onSearch: (value: string) => void;
    onSelect: (option: ILookupOption) => void;
}> = ({ label, placeholder, search, selected, options, disabled, isOpen, onOpen, onSearch, onSelect }) => (
    <label style={styles.label}>
        {label}
        <div style={styles.picker}>
            <LeaseRecordInput
                value={search}
                disabled={disabled}
                placeholder={placeholder}
                onFocus={onOpen}
                onClick={onOpen}
                onChange={(e) => onSearch(e.target.value)}
                style={styles.input}
                autoComplete="off"
            />
            {isOpen && !selected && (
                <div style={styles.options}>
                    {options.length === 0 && <div style={styles.noOptions}>No matching records</div>}
                    {options.map((option) => (
                        <button type="button" key={option.value} style={styles.option} onClick={() => onSelect(option)}>
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    </label>
);

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 100,
        background: 'rgba(17,24,39,.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modal: { width: '620px', maxWidth: 'calc(100vw - 2rem)', background: '#fff', borderRadius: '8px', boxShadow: '0 20px 40px rgba(0,0,0,.25)' },
    header: { display: 'flex', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb' },
    title: { margin: 0, fontSize: '1.25rem' },
    subtitle: { margin: '.35rem 0 0', color: '#4b5563', fontSize: '.875rem' },
    close: { border: 0, background: 'transparent', fontSize: '1.75rem', cursor: 'pointer', color: '#6b7280' },
    body: { padding: '1.25rem 1.5rem' },
    help: { margin: '0 0 1rem', padding: '.75rem', background: '#eff6ff', color: '#1e3a8a', borderRadius: '4px', fontSize: '.875rem' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    label: { display: 'flex', flexDirection: 'column', gap: '.35rem', fontSize: '.875rem', fontWeight: 600, color: '#374151' },
    input: { height: '38px', padding: '0 .65rem', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff' },
    picker: { position: 'relative', display: 'flex', flexDirection: 'column' },
    options: {
        position: 'absolute',
        top: '40px',
        left: 0,
        right: 0,
        zIndex: 5,
        maxHeight: '180px',
        overflowY: 'auto',
        background: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        boxShadow: '0 8px 16px rgba(0,0,0,.12)',
    },
    option: {
        padding: '.65rem',
        border: 0,
        borderBottom: '1px solid #f3f4f6',
        background: '#fff',
        textAlign: 'left',
        cursor: 'pointer',
        width: '100%',
    },
    noOptions: { padding: '.65rem', color: '#6b7280', fontWeight: 400 },
    error: { marginTop: '1rem', padding: '.75rem', color: '#991b1b', background: '#fef2f2', borderRadius: '4px' },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: '.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' },
    secondary: { padding: '.55rem 1rem', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer' },
    primary: { padding: '.55rem 1rem', border: 0, borderRadius: '4px', background: '#2563eb', color: '#fff', cursor: 'pointer' },
};

export default CreateManualLeaseModal;



// WEBPACK FOOTER //
// ./src/components/leases/CreateManualLeaseModal.tsx